export const ENV_SEGMENTS: ReadonlySet<string> = new Set([
  'staging',
  'dev',
  'demo',
  'qa',
  'black',
  'blue',
  'brown',
  'green',
  'orange',
  'pink',
  'purple',
  'red',
  'white',
  'yellow',
]);

export interface ParsedHostname {
  tenant: string;
  envSegment: string;
  hasEnvSegment: boolean;
}

export function parseHostname(hostname: string): ParsedHostname {
  const parts = hostname.split('.');
  const tenant = parts[0] ?? '';
  const rawEnv = parts[1] ?? '';
  const dashIdx = rawEnv.indexOf('-');
  const envSegment =
    dashIdx !== -1 && ENV_SEGMENTS.has(rawEnv.slice(dashIdx + 1))
      ? rawEnv.slice(dashIdx + 1)
      : rawEnv;
  const hasEnvSegment = parts.length > 3 && ENV_SEGMENTS.has(envSegment);
  return { tenant, envSegment, hasEnvSegment };
}

export function isProductionEnvironment(): boolean {
  if (import.meta.env.DEV) return false;
  return !parseHostname(window.location.hostname).hasEnvSegment;
}

/**
 * Resolve the deployment environment label from the runtime hostname. Single
 * source for the Sentry `environment` tag and the #app `data-env` marker — the
 * app builds once with no per-env target, so a build-time `VITE_ENV` is empty
 * and cannot distinguish deploys.
 * - dev build                                              -> VITE_LOCAL_ENV, or 'local' when unset
 * - deployed, env segment present (e.g. x.staging.schedule.online) -> the segment ('staging')
 * - deployed, no env segment (e.g. x.schedule.online)             -> 'production'
 */
export function resolveRuntimeEnv(): string {
  if (import.meta.env.DEV) return import.meta.env.VITE_LOCAL_ENV || 'local';
  const { envSegment, hasEnvSegment } = parseHostname(window.location.hostname);
  return hasEnvSegment ? envSegment : 'production';
}
